import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useSnackbar } from "notistack";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import {
  createEmptyWorkflow,
  getActionLabel,
  getDynamicFieldsForTracker,
  getTriggerLabel,
  groupDynamicFields,
  saveWorkflowOnAutomationViaApi,
  TAG_MODE_OPTIONS,
  toDynamicToken,
} from "../automations/automationStorage";
import EmailContentEditor from "../automations/EmailContentEditor";
import {
  BORDER,
  EndMarker,
  ErrorText,
  FieldLabel,
  INK,
  inputSx,
  LINE,
  MUTED,
  OptionTile,
  Panel,
  PillGroup,
  PRIMARY,
  PRIMARY_SOFT,
  StepCard,
} from "./builderUi";
import TagChipPicker from "../TagChipPicker";
import {
  applyTriggerChange,
  coachReviewSupport,
  emptyTriggerConfig,
  insertFieldsFor,
  isBuilderTrigger,
  recipientsForAudience,
  schedulePointsOf,
  triggerConfigFromWorkflow,
  triggerPatch,
  TriggerFields,
  validateTriggerConfig,
} from "./triggerConfig";
import {
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS,
  uploadEmailAttachment,
} from "./attachmentApi";
import { appendWorkflowOnAutomationViaApi } from "./workflowApi";

const ACTION_ICONS = {
  email: MailOutlineIcon,
  record_event: HistoryOutlinedIcon,
  notify: NotificationsNoneOutlinedIcon,
  create_coach_review: RateReviewOutlinedIcon,
  assign_status: LabelOutlinedIcon,
  assign_tags: LocalOfferOutlinedIcon,
};

const WORKFLOW_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
];

const INSERT_TARGETS = [
  { value: "subject", label: "Subject" },
  { value: "message", label: "Message" },
];

const WAIT_UNIT_OPTIONS = [
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
];

const MAX_CC = 10;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "" when the CC list is fine, otherwise the error to show. */
const ccError = (value) => {
  const list = String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  if (list.length > MAX_CC) return `Up to ${MAX_CC} CC emails`;
  if (list.some((item) => !EMAIL_RE.test(item))) {
    return "Enter valid emails separated by commas";
  }
  return "";
};

const newStepId = () =>
  `step_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const createActionStep = (overrides = {}) => ({
  id: newStepId(),
  type: "action",
  action: "email",
  recipients: "person",
  cc: "",
  attachments: [],
  subject: "",
  message: "",
  assignStatusName: "",
  assignTags: [],
  tagMode: "add",
  ...overrides,
});

const createWaitStep = () => ({
  id: newStepId(),
  type: "wait",
  amount: 1,
  unit: "days",
});

/** Saved steps, or the legacy single action as step 1. */
const stepsFromWorkflow = (workflow) => {
  if (Array.isArray(workflow?.steps) && workflow.steps.length) {
    return workflow.steps.map((step) => ({
      ...step,
      id: step.id || newStepId(),
    }));
  }
  return [
    createActionStep({
      action: workflow?.action || "email",
      recipients: workflow?.recipients || "person",
      subject: workflow?.subject || "",
      message: workflow?.message || "",
      assignStatusName: workflow?.assignStatusName || "",
      assignTags: Array.isArray(workflow?.assignTags) ? workflow.assignTags : [],
      tagMode: workflow?.tagMode || "add",
      attachments: Array.isArray(workflow?.attachments)
        ? workflow.attachments
        : [],
      cc: workflow?.cc || "",
    }),
  ];
};

const stepTitle = (step) => {
  if (step.type === "wait") return "Wait";
  const action = step.action || "email";
  return action === "email" ? "Send email" : getActionLabel(action);
};

const TokenChip = ({ label, token, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    title={toDynamicToken(token)}
    sx={{
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      borderRadius: "6px",
      px: 1,
      py: 0.35,
      fontFamily: "inherit",
      fontSize: 12,
      fontWeight: 500,
      color: "#475569",
      cursor: "pointer",
      lineHeight: 1.35,
      "&:hover": {
        borderColor: PRIMARY,
        bgcolor: PRIMARY_SOFT,
        color: PRIMARY,
      },
    }}
  >
    {label}
  </Box>
);

/** "+" between steps — adds a Send email or Wait step there. */
const AddStepButton = ({ onAdd }) => {
  const [anchor, setAnchor] = useState(null);
  const pick = (type) => {
    setAnchor(null);
    onAdd(type);
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 0.25,
      }}
    >
      <Box sx={{ width: 2, height: 14, bgcolor: LINE }} />
      <Tooltip title="Add step">
        <IconButton
          aria-label="Add step"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            width: 30,
            height: 30,
            bgcolor: PRIMARY,
            color: "#fff",
            "&:hover": { bgcolor: PRIMARY, opacity: 0.9 },
          }}
        >
          <AddRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <Box sx={{ width: 2, height: 14, bgcolor: LINE }} />
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        <MenuItem onClick={() => pick("email")} sx={{ gap: 1, fontSize: 14 }}>
          <MailOutlineIcon sx={{ fontSize: 18, color: MUTED }} />
          Send email
        </MenuItem>
        <MenuItem
          onClick={() => pick("assign_tags")}
          sx={{ gap: 1, fontSize: 14 }}
        >
          <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
          Assign tags
        </MenuItem>
        <MenuItem
          onClick={() => pick("assign_status")}
          sx={{ gap: 1, fontSize: 14 }}
        >
          <LabelOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
          Assign status
        </MenuItem>
        <MenuItem onClick={() => pick("wait")} sx={{ gap: 1, fontSize: 14 }}>
          <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
          Wait
        </MenuItem>
      </Menu>
    </Box>
  );
};

/** Card for one step: icon, title, "Step N", move up/down, delete. */
const StepShell = ({
  icon,
  title,
  index,
  isFirst,
  isLast,
  onMove,
  onDelete,
  children,
}) => (
  <Panel sx={{ overflow: "hidden" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 2,
        py: 1.25,
        borderBottom: children ? `1px solid ${BORDER}` : 0,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          bgcolor: PRIMARY_SOFT,
          color: PRIMARY,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: INK }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: MUTED }}>
        Step {index + 1}
      </Typography>
      <Box sx={{ ml: "auto", display: "flex", gap: 0.25 }}>
        <Tooltip title="Move up">
          <span>
            <IconButton
              size="small"
              aria-label="Move up"
              disabled={isFirst}
              onClick={() => onMove(-1)}
              sx={{ color: MUTED }}
            >
              <ArrowUpwardRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton
              size="small"
              aria-label="Move down"
              disabled={isLast}
              onClick={() => onMove(1)}
              sx={{ color: MUTED }}
            >
              <ArrowDownwardRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            aria-label="Delete step"
            onClick={onDelete}
            sx={{ color: MUTED, "&:hover": { color: "#dc2626" } }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
    {children ? <Box sx={{ p: { xs: 1.75, sm: 2 } }}>{children}</Box> : null}
  </Panel>
);

/** Uploaded files on an email step + the "Attach a file" button. */
const AttachmentsField = ({ attachments, onChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const files = Array.isArray(attachments) ? attachments : [];

  const handleFiles = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;
    const room = MAX_ATTACHMENTS - files.length;
    if (picked.length > room) {
      enqueueSnackbar(`Up to ${MAX_ATTACHMENTS} files`, { variant: "warning" });
    }
    const allowed = picked.slice(0, Math.max(0, room));
    const tooBig = allowed.filter((file) => file.size > MAX_ATTACHMENT_BYTES);
    if (tooBig.length) {
      enqueueSnackbar("Each file can be up to 10 MB", { variant: "warning" });
    }
    const toUpload = allowed.filter(
      (file) => file.size <= MAX_ATTACHMENT_BYTES
    );
    if (!toUpload.length) return;

    setUploading(true);
    const added = [];
    for (const file of toUpload) {
      try {
        added.push(await uploadEmailAttachment(file));
      } catch (err) {
        enqueueSnackbar(err?.message || `Could not upload ${file.name}`, {
          variant: "error",
        });
      }
    }
    setUploading(false);
    if (added.length) onChange([...files, ...added]);
  };

  return (
    <>
      <FieldLabel sx={{ mt: 2 }}>Attachments</FieldLabel>
      {files.length ? (
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 1 }}
        >
          {files.map((file, i) => (
            <Box
              key={`${file.path || file.url}-${i}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.6,
                border: `1px solid ${BORDER}`,
                borderRadius: "8px",
                bgcolor: "#fff",
              }}
            >
              <InsertDriveFileOutlinedIcon
                sx={{ fontSize: 18, color: MUTED }}
              />
              <Typography
                component="a"
                href={file.url}
                target="_blank"
                rel="noreferrer"
                sx={{
                  fontSize: 13,
                  color: INK,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {file.name || "Attachment"}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Remove ${file.name || "attachment"}`}
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                sx={{ color: MUTED }}
              >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={handleFiles}
        data-testid="attachment-input"
      />
      <button
        type="button"
        className="small-contained-outlined-button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || files.length >= MAX_ATTACHMENTS}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <AttachFileRoundedIcon sx={{ fontSize: 17 }} />
        {uploading ? "Uploading…" : "Attach a file"}
      </button>
    </>
  );
};

/** CC, Subject, Body, Insert field and Attachments for one email step. */
const MessageStepFields = ({
  step,
  idPrefix,
  dynamicFields,
  ccErrorText,
  subjectErrorText,
  messageErrorText,
  onChange,
}) => {
  const [insertTarget, setInsertTarget] = useState("message");
  const [tokenSearch, setTokenSearch] = useState("");
  const subjectRef = useRef(null);
  const messageRef = useRef(null);
  const editorRef = useRef(null);

  const isEmail = (step.action || "email") === "email";

  const groupEntries = useMemo(() => {
    const q = tokenSearch.trim().toLowerCase();
    const source = q
      ? dynamicFields.filter(
          (field) =>
            field.label?.toLowerCase().includes(q) ||
            field.token?.toLowerCase().includes(q)
        )
      : dynamicFields;
    return Object.entries(groupDynamicFields(source));
  }, [dynamicFields, tokenSearch]);

  const focusTarget = (target) => {
    setInsertTarget(target);
    if (target === "subject") subjectRef.current?.focus();
    else if (isEmail) editorRef.current?.focus();
    else messageRef.current?.focus();
  };

  const insertToken = (token) => {
    const marker = toDynamicToken(token);
    if (insertTarget === "message" && isEmail && editorRef.current) {
      editorRef.current.insertContent(marker);
      return;
    }
    const field = insertTarget === "subject" ? "subject" : "message";
    const el =
      insertTarget === "subject" ? subjectRef.current : messageRef.current;
    const current = step[field] || "";
    if (el && typeof el.selectionStart === "number") {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      onChange({
        [field]: current.slice(0, start) + marker + current.slice(end),
      });
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + marker.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      onChange({ [field]: `${current}${marker}` });
    }
  };

  return (
    <>
      {isEmail ? (
        <>
          <FieldLabel htmlFor={`${idPrefix}-cc`}>CC (optional)</FieldLabel>
          <TextField
            id={`${idPrefix}-cc`}
            fullWidth
            size="small"
            placeholder="e.g. office@example.com, manager@example.com"
            value={step.cc || ""}
            onChange={(e) => onChange({ cc: e.target.value })}
            error={Boolean(ccErrorText)}
            sx={inputSx}
          />
          {ccErrorText ? <ErrorText>{ccErrorText}</ErrorText> : null}
        </>
      ) : null}

      <FieldLabel htmlFor={`${idPrefix}-subject`} sx={{ mt: isEmail ? 2 : 0 }}>
        Subject
      </FieldLabel>
      <TextField
        id={`${idPrefix}-subject`}
        fullWidth
        size="small"
        value={step.subject || ""}
        onChange={(e) => onChange({ subject: e.target.value })}
        inputRef={subjectRef}
        onFocus={() => setInsertTarget("subject")}
        error={Boolean(subjectErrorText)}
        sx={inputSx}
      />
      {subjectErrorText ? <ErrorText>{subjectErrorText}</ErrorText> : null}

      <FieldLabel htmlFor={`${idPrefix}-message`} sx={{ mt: 2 }}>
        Body
      </FieldLabel>
      {isEmail ? (
        <EmailContentEditor
          value={step.message || ""}
          onChange={(html) => onChange({ message: html, bodyFormat: "html" })}
          onEditorReady={(editor) => {
            editorRef.current = editor;
          }}
          onFocus={() => setInsertTarget("message")}
          height={280}
        />
      ) : (
        <TextField
          id={`${idPrefix}-message`}
          fullWidth
          multiline
          minRows={6}
          value={step.message || ""}
          onChange={(e) => onChange({ message: e.target.value })}
          inputRef={messageRef}
          onFocus={() => setInsertTarget("message")}
          error={Boolean(messageErrorText)}
          sx={inputSx}
        />
      )}
      {messageErrorText ? <ErrorText>{messageErrorText}</ErrorText> : null}

      <Box
        sx={{
          mt: 1.75,
          p: 1.5,
          borderRadius: "10px",
          bgcolor: "#f8fafc",
          border: `1px solid ${BORDER}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <FieldLabel sx={{ mb: 0 }}>Insert field</FieldLabel>
          <PillGroup
            size="small"
            ariaLabel="Insert field into"
            options={INSERT_TARGETS}
            value={insertTarget}
            onChange={focusTarget}
          />
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder="Search fields…"
          value={tokenSearch}
          onChange={(e) => setTokenSearch(e.target.value)}
          sx={{ ...inputSx, mb: 1.25 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.65,
            maxHeight: 160,
            overflowY: "auto",
          }}
        >
          {groupEntries.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              No fields match.
            </Typography>
          ) : (
            groupEntries.flatMap(([, fields]) =>
              fields.map((field) => (
                <TokenChip
                  key={field.token}
                  label={field.label}
                  token={field.token}
                  onClick={() => insertToken(field.token)}
                />
              ))
            )
          )}
        </Box>
      </Box>

      {isEmail ? (
        <AttachmentsField
          attachments={step.attachments}
          onChange={(attachments) => onChange({ attachments })}
        />
      ) : null}
    </>
  );
};

/**
 * Flow-style workflow editor: Trigger → steps (Send email / Wait) → End.
 * The trigger is For coach / member → review, reminder or New Submission;
 * reminders also take before / after the due date + days. Steps are saved on the workflow, with the first
 * email mirrored onto the workflow's own action / subject / message.
 */
export default function WorkflowBuilderPanel({
  automation,
  tracker,
  workflowId,
  isNew,
  initialName = "",
  onSaved,
  onCancel,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(createEmptyWorkflow);
  const [trigger, setTrigger] = useState(emptyTriggerConfig);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Load the draft when the selected workflow changes — not on every parent
  // save, so unsaved edits here survive an automation name/enabled save.
  const automationRef = useRef(automation);
  automationRef.current = automation;
  const automationId = automation?.id;

  useEffect(() => {
    const current = automationRef.current;
    if (!current) return;
    if (isNew) {
      const base = createEmptyWorkflow();
      setForm({
        ...base,
        name: initialName,
        action: "email",
        steps: [
          createActionStep({ subject: base.subject, message: base.message }),
        ],
      });
      setTrigger(emptyTriggerConfig());
    } else {
      const existing = (current.workflows || []).find(
        (item) => item.id === workflowId
      );
      setForm(
        existing
          ? {
              ...createEmptyWorkflow(),
              ...existing,
              steps: stepsFromWorkflow(existing),
            }
          : null
      );
      setTrigger(
        existing ? triggerConfigFromWorkflow(existing) : emptyTriggerConfig()
      );
    }
    setErrors({});
    // initialName only matters for a new draft; the parent re-keys the
    // panel for each one.
  }, [automationId, workflowId, isNew]);

  // Insert-field chips that have a value for the chosen trigger.
  const trackerFields = useMemo(
    () => getDynamicFieldsForTracker(automation?.trackerId),
    [automation?.trackerId]
  );
  const dynamicFields = useMemo(
    () => insertFieldsFor(trigger, trackerFields),
    [trigger, trackerFields]
  );

  const savedWorkflow = useMemo(
    () =>
      (automation?.workflows || []).find((item) => item.id === workflowId) ||
      null,
    [automation, workflowId]
  );

  // Journey schedule points of the automation's tracker (Week 1, Week 2…).
  const schedulePoints = useMemo(
    () => schedulePointsOf(tracker, savedWorkflow),
    [tracker, savedWorkflow]
  );

  // Coach reviews this tracker can actually send (none on lead trackers).
  const reviewSupport = useMemo(() => coachReviewSupport(tracker), [tracker]);

  const trackerStatusNames = useMemo(() => {
    const names = (Array.isArray(tracker?.statuses) ? tracker.statuses : [])
      .map((s) => String(s?.name || s?.label || "").trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [tracker]);

  const trackerTagOptions = useMemo(() => {
    const names = (Array.isArray(tracker?.tags) ? tracker.tags : [])
      .map((t) => String(t || "").trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [tracker]);

  const update = (patch) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const changeTrigger = (patch) => {
    setTrigger((prev) => applyTriggerChange(prev, patch));
    setErrors((prev) => ({
      ...prev,
      audience: "",
      triggerId: "",
      dueTiming: "",
      dueDays: "",
    }));
  };

  const setSteps = (change) => {
    setForm((prev) =>
      prev ? { ...prev, steps: change(prev.steps || []) } : prev
    );
    setErrors((prev) => ({ ...prev, steps: "" }));
  };

  // Step errors are keyed by position, so drop them when steps shift.
  const clearStepErrors = () =>
    setErrors((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(
          ([key]) => !/^(cc|subject|message)-\d+$/.test(key)
        )
      )
    );

  const addStep = (index, type) => {
    setSteps((steps) => {
      let step;
      if (type === "wait") step = createWaitStep();
      else if (type === "assign_tags") {
        step = createActionStep({ action: "assign_tags", tagMode: "add" });
      } else if (type === "assign_status") {
        step = createActionStep({ action: "assign_status" });
      } else {
        step = createActionStep({ action: "email" });
      }
      return [...steps.slice(0, index), step, ...steps.slice(index)];
    });
    clearStepErrors();
  };

  const updateStep = (index, patch) => {
    setSteps((steps) =>
      steps.map((step, i) => (i === index ? { ...step, ...patch } : step))
    );
    const edited = ["cc", "subject", "message"].filter((key) => key in patch);
    if (edited.length) {
      setErrors((prev) => {
        const next = { ...prev };
        edited.forEach((key) => {
          next[`${key}-${index}`] = "";
        });
        return next;
      });
    }
  };

  const moveStep = (index, delta) => {
    setSteps((steps) => {
      const target = index + delta;
      if (target < 0 || target >= steps.length) return steps;
      const next = [...steps];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    clearStepErrors();
  };

  const removeStep = (index) => {
    setSteps((steps) => steps.filter((_, i) => i !== index));
    clearStepErrors();
  };

  // Step waiting for "Delete this step?" to be confirmed.
  const [stepToDelete, setStepToDelete] = useState(null);

  // The name is edited from the workflow list, so the saved one wins.
  const workflowName = String(
    (isNew ? form?.name : savedWorkflow?.name ?? form?.name) || ""
  ).trim();

  // Older workflows keep their trigger until a new one is picked here.
  const keepsLegacyTrigger =
    !isNew && !trigger.audience && !isBuilderTrigger(form);

  const validate = () => {
    const next = keepsLegacyTrigger
      ? {}
      : validateTriggerConfig(trigger, reviewSupport);
    if (!workflowName) next.name = "Name is required";
    if (!(form?.steps || []).some((step) => step.type === "action")) {
      next.steps = "Add at least one action step";
    }
    (form?.steps || []).forEach((step, index) => {
      if (step.type !== "action") return;
      const action = step.action || "email";
      if (action === "assign_tags") {
        const tags = Array.isArray(step.assignTags) ? step.assignTags : [];
        if ((step.tagMode || "add") !== "set" && !tags.length) {
          next[`tags-${index}`] = "Pick at least one tag";
        }
        return;
      }
      if (action !== "email" && action !== "notify") return;
      const message = ccError(step.cc);
      if (message) next[`cc-${index}`] = message;
      if (action !== "email") return;
      if (!String(step.subject || "").trim()) {
        next[`subject-${index}`] = "Subject is required";
      }
      if (!String(step.message || "").trim()) {
        next[`message-${index}`] = "Body is required";
      }
    });
    setErrors(next);
    if (next.name) enqueueSnackbar(next.name, { variant: "warning" });
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!automation?.id || !form) return;
    if (!validate()) return;
    const triggerFields = keepsLegacyTrigger
      ? {}
      : triggerPatch(trigger, schedulePoints);
    // Coach workflows email the coach, member workflows the member.
    const stepRecipients = keepsLegacyTrigger
      ? null
      : recipientsForAudience(trigger.audience);
    const steps = (form.steps || []).map((step) =>
      step.type === "wait"
        ? {
            id: step.id,
            type: "wait",
            amount: Math.max(1, Math.floor(Number(step.amount) || 1)),
            unit: step.unit || "days",
          }
        : {
            ...step,
            action: step.action || "email",
            subject: (step.subject || "").trim(),
            message: (step.message || "").trim(),
            bodyFormat:
              (step.action || "email") === "email"
                ? step.bodyFormat || "html"
                : step.bodyFormat,
            cc: String(step.cc || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
              .join(", "),
            attachments: Array.isArray(step.attachments)
              ? step.attachments
              : [],
            assignTags: Array.isArray(step.assignTags) ? step.assignTags : [],
            tagMode: step.tagMode || "add",
            assignStatusName: step.assignStatusName || "",
            recipients: stepRecipients || step.recipients || "person",
          }
    );
    // The first action also lives on the workflow itself, so the list,
    // the old automation screen and single-action runs keep working.
    const firstAction = steps.find((step) => step.type === "action") || {};
    setSaving(true);
    let saved;
    // New workflows go to the end of the list.
    const saveWorkflow = isNew
      ? appendWorkflowOnAutomationViaApi
      : saveWorkflowOnAutomationViaApi;
    try {
      saved = await saveWorkflow(automation.id, {
        ...form,
        ...triggerFields,
        name: workflowName,
        description: (form.description || "").trim(),
        steps,
        action: firstAction.action || "email",
        recipients: firstAction.recipients || "person",
        subject: firstAction.subject || "",
        message: firstAction.message || "",
        bodyFormat: firstAction.bodyFormat,
        attachments: firstAction.attachments || [],
        assignStatusName:
          firstAction.assignStatusName || form.assignStatusName || "",
        assignTags: firstAction.assignTags || form.assignTags || [],
        tagMode: firstAction.tagMode || form.tagMode || "add",
        requireAnyTag: Array.isArray(form.requireAnyTag)
          ? form.requireAnyTag
          : [],
      });
    } catch (err) {
      setSaving(false);
      enqueueSnackbar(err?.message || "Could not save workflow", {
        variant: "error",
      });
      return;
    }
    const workflow =
      (saved.workflows || []).find((item) => item.id === form.id) ||
      (saved.workflows || []).find((item) => item.name === workflowName) ||
      (saved.workflows || [])[0];
    enqueueSnackbar(isNew ? "Workflow created" : "Workflow saved", {
      variant: "success",
    });
    // Reset local state before the parent re-keys this panel.
    setSaving(false);
    onSaved?.(saved, workflow);
  };

  if (!form) {
    return (
      <Panel sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 14, color: MUTED }}>
          Workflow not found.
        </Typography>
      </Panel>
    );
  }

  const steps = form.steps || [];
  const legacyTriggerLabel =
    !isBuilderTrigger(form) && form.triggerId
      ? (form.triggerModel || "event") === "relative"
        ? getTriggerLabel("relative_offset")
        : (form.triggerModel || "event") === "calendar"
        ? getTriggerLabel("calendar_date")
        : getTriggerLabel(form.triggerId)
      : "";

  const renderStepBody = (step, index) => {
    if (step.type === "wait") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            fontSize: 14,
            color: INK,
          }}
        >
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>
            Wait
          </Typography>
          <TextField
            size="small"
            type="number"
            inputProps={{ min: 1, "aria-label": "Wait amount" }}
            value={step.amount ?? 1}
            onChange={(e) =>
              updateStep(index, {
                amount:
                  e.target.value === ""
                    ? ""
                    : Math.max(1, Math.floor(Number(e.target.value)) || 1),
              })
            }
            sx={{ ...inputSx, width: 96 }}
          />
          <TextField
            select
            size="small"
            value={step.unit || "days"}
            onChange={(e) => updateStep(index, { unit: e.target.value })}
            inputProps={{ "aria-label": "Wait unit" }}
            sx={{ ...inputSx, width: 130 }}
          >
            {WAIT_UNIT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography component="span" sx={{ fontSize: 13, color: MUTED }}>
            then continue
          </Typography>
        </Box>
      );
    }

    const action = step.action || "email";
    if (action === "email" || action === "notify") {
      return (
        <MessageStepFields
          step={step}
          idPrefix={`workflow-step-${index}`}
          dynamicFields={dynamicFields}
          ccErrorText={errors[`cc-${index}`]}
          subjectErrorText={errors[`subject-${index}`]}
          messageErrorText={errors[`message-${index}`]}
          onChange={(patch) => updateStep(index, patch)}
        />
      );
    }
    if (action === "assign_status") {
      const current = String(step.assignStatusName || "").trim();
      const names =
        current && !trackerStatusNames.includes(current)
          ? [...trackerStatusNames, current]
          : trackerStatusNames;
      return (
        <>
          <FieldLabel htmlFor={`workflow-step-${index}-status`}>
            Status to assign
          </FieldLabel>
          {trackerStatusNames.length ? (
            <PillGroup
              ariaLabel="Status to assign"
              options={names.map((name) => ({ value: name, label: name }))}
              value={step.assignStatusName || ""}
              onChange={(value) =>
                updateStep(index, { assignStatusName: value })
              }
            />
          ) : (
            <TextField
              id={`workflow-step-${index}-status`}
              fullWidth
              size="small"
              value={step.assignStatusName || ""}
              onChange={(e) =>
                updateStep(index, { assignStatusName: e.target.value })
              }
              placeholder="Must match a status on this tracker"
              sx={inputSx}
            />
          )}
        </>
      );
    }
    if (action === "assign_tags") {
      return (
        <>
          <FieldLabel>Tag mode</FieldLabel>
          <PillGroup
            ariaLabel="Tag mode"
            options={TAG_MODE_OPTIONS}
            value={step.tagMode || "add"}
            onChange={(value) => updateStep(index, { tagMode: value })}
          />
          <Box sx={{ mt: 2 }}>
            <TagChipPicker
              label="Tags"
              options={trackerTagOptions}
              value={step.assignTags || []}
              onChange={(assignTags) => updateStep(index, { assignTags })}
              emptyLabel="Add tags on the journey first (Tracker → Tags)."
            />
            {errors[`tags-${index}`] ? (
              <ErrorText>{errors[`tags-${index}`]}</ErrorText>
            ) : null}
          </Box>
        </>
      );
    }
    return null;
  };

  return (
    <Box>
      {/* Trigger */}
      <StepCard
        tone="warm"
        icon={<FlashOnOutlinedIcon sx={{ fontSize: 19 }} />}
        title="Trigger"
      >
        {legacyTriggerLabel && !trigger.audience ? (
          <Box sx={{ mb: 2, maxWidth: 360 }}>
            <OptionTile
              selected
              title={legacyTriggerLabel}
              onClick={() => {}}
            />
          </Box>
        ) : null}
        <TriggerFields
          value={trigger}
          onChange={changeTrigger}
          errors={errors}
          idPrefix="workflow-trigger"
          schedulePoints={schedulePoints}
          support={reviewSupport}
        />
      </StepCard>

      {/* Steps: Send email / Wait */}
      <AddStepButton onAdd={(type) => addStep(0, type)} />
      {steps.map((step, index) => {
        const Icon =
          step.type === "wait"
            ? AccessTimeOutlinedIcon
            : ACTION_ICONS[step.action || "email"] || MailOutlineIcon;
        return (
          <React.Fragment key={step.id}>
            <StepShell
              icon={<Icon sx={{ fontSize: 19 }} />}
              title={stepTitle(step)}
              index={index}
              isFirst={index === 0}
              isLast={index === steps.length - 1}
              onMove={(delta) => moveStep(index, delta)}
              onDelete={() => setStepToDelete(index)}
            >
              {renderStepBody(step, index)}
            </StepShell>
            <AddStepButton onAdd={(type) => addStep(index + 1, type)} />
          </React.Fragment>
        );
      })}
      {errors.steps ? (
        <ErrorText sx={{ textAlign: "center", mt: 0 }}>
          {errors.steps}
        </ErrorText>
      ) : null}

      <EndMarker />

      {/* Sticky save bar */}
      <Box
        sx={{
          position: "sticky",
          bottom: 12,
          zIndex: 3,
          mt: 2,
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 1,
          bgcolor: "rgba(255, 255, 255, 0.97)",
          border: `1px solid ${BORDER}`,
          borderRadius: "12px",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Box sx={{ mr: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <FieldLabel sx={{ mb: 0 }}>Status</FieldLabel>
          <PillGroup
            size="small"
            ariaLabel="Status"
            options={WORKFLOW_STATUS_OPTIONS}
            value={form.status === "draft" ? "draft" : "active"}
            onChange={(value) => update({ status: value })}
          />
        </Box>
        {onCancel ? (
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          className="small-contained-button"
          onClick={handleSave}
          disabled={saving}
          style={{ opacity: saving ? 0.6 : 1 }}
        >
          {isNew ? "Save workflow" : "Update"}
        </button>
      </Box>

      <CustomConfirmation
        openDelete={stepToDelete !== null}
        setOpenDelete={(open) => {
          if (!open) setStepToDelete(null);
        }}
        title="Delete this step?"
        handleAgree={() => {
          removeStep(stepToDelete);
          setStepToDelete(null);
        }}
        open={false}
        setOpen={() => {}}
      />
    </Box>
  );
}
